import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { taskAPI, caseFileAPI, userAPI } from '../api/api';
import { TASK_STATUS, TASK_STATUS_LABELS } from '../utils/constants';
import { toISODateOrNull, getTodayDateString, formatDateForDisplay } from '../utils/dateUtils';
import TaskForm from '../components/Tasks/TaskForm';
import ActionIcon from '../components/Common/ActionIcon';
import './Tasks.css';
import { onayla } from '../utils/onayla';
import BosDurum from '../components/Common/BosDurum';
import { YENI_TASARIM } from '../utils/yeniTasarim';

const Tasks = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [filteredTasks, setFilteredTasks] = useState([]);
  const [caseFiles, setCaseFiles] = useState([]);
  const [users, setUsers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [editingTaskMode, setEditingTaskMode] = useState('complete'); // 'complete' | 'edit'
  const [rejectingTask, setRejectingTask] = useState(null);
  const [rejectForm, setRejectForm] = useState({
    rejectionReason: '',
    newDueDate: ''
  });
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    search: ''
  });
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');

  const isAdmin = () => {
    if (!user) return false;
    const role = user.Role ?? user.role ?? '';
    return role === 'Admin';
  };

  useEffect(() => {
    if (token) {
      loadData();
    }
  }, [token]);

  useEffect(() => {
    const handleTaskUpdated = () => loadData();
    window.addEventListener('taskUpdated', handleTaskUpdated);
    return () => window.removeEventListener('taskUpdated', handleTaskUpdated);
  }, []);

  useEffect(() => {
    applyFilters();
  }, [tasks, filters, filterStatus]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load tasks (all for admin, my tasks for lawyers)
      const tasksData = isAdmin() 
        ? await taskAPI.getAll() 
        : await taskAPI.getMyTasks();
      setTasks(tasksData);
      
      // Load case files and users for admin (to create tasks)
      if (isAdmin()) {
        const [filesData, usersData] = await Promise.all([
          caseFileAPI.getAll(),
          userAPI.getAll()
        ]);
        setCaseFiles(filesData);
        setUsers(usersData);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      alert('Veriler yüklenirken hata oluştu: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...tasks];

    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(task => {
        const foyNo = task.FoyNo ?? task.foyNo ?? '';
        const muvekkil = task.Muvekkil ?? task.muvekkil ?? '';
        const description = task.Description ?? task.description ?? '';
        return (
          foyNo.toString().includes(searchLower) ||
          muvekkil.toLowerCase().includes(searchLower) ||
          description.toLowerCase().includes(searchLower)
        );
      });
    }

    if (filterStatus !== 'all') {
      const statusNum = parseInt(filterStatus, 10);
      filtered = filtered.filter(task => {
        const status = task.Status ?? task.status ?? 1;
        return status === statusNum;
      });
    }

    setFilteredTasks(filtered);
  };

  const pendingReviewCount = useMemo(() => 
    tasks.filter(t => (t.Status ?? t.status) === TASK_STATUS.UnderReview).length, 
    [tasks]
  );
  const pendingCompleteCount = useMemo(() => 
    tasks.filter(t => (t.Status ?? t.status) === TASK_STATUS.InProgress).length, 
    [tasks]
  );

  const tabCounts = useMemo(() => {
    const inProgress = tasks.filter(t => (t.Status ?? t.status) === TASK_STATUS.InProgress).length;
    const underReview = tasks.filter(t => (t.Status ?? t.status) === TASK_STATUS.UnderReview).length;
    const completed = tasks.filter(t => (t.Status ?? t.status) === TASK_STATUS.Completed).length;
    const rejected = tasks.filter(t => (t.Status ?? t.status) === TASK_STATUS.Rejected).length;
    return { all: tasks.length, inProgress, underReview, completed, rejected };
  }, [tasks]);

  const sortedTasks = useMemo(() => {
    if (!sortColumn) return filteredTasks;
    const list = [...filteredTasks];
    const admin = user && (user.Role === 'Admin' || user.role === 'Admin');
    const cmp = (a, b) => {
      switch (sortColumn) {
        case 'foyNo': {
          const va = String(a.FoyNo ?? a.foyNo ?? '').toLowerCase();
          const vb = String(b.FoyNo ?? b.foyNo ?? '').toLowerCase();
          return va.localeCompare(vb, 'tr');
        }
        case 'muvekkil': {
          const va = (a.Muvekkil ?? a.muvekkil ?? '').toLowerCase();
          const vb = (b.Muvekkil ?? b.muvekkil ?? '').toLowerCase();
          return va.localeCompare(vb, 'tr');
        }
        case 'description': {
          const va = (a.Description ?? a.description ?? '').toLowerCase();
          const vb = (b.Description ?? b.description ?? '').toLowerCase();
          return va.localeCompare(vb, 'tr');
        }
        case 'dueDate': {
          const ta = (a.DueDate ?? a.dueDate) ? new Date(a.DueDate ?? a.dueDate).getTime() : 0;
          const tb = (b.DueDate ?? b.dueDate) ? new Date(b.DueDate ?? b.dueDate).getTime() : 0;
          return ta - tb;
        }
        case 'assigned': {
          const va = (admin ? (a.AssignedToUserName ?? a.assignedToUserName) : (a.AssignedByUserName ?? a.assignedByUserName)) ?? '';
          const vb = (admin ? (b.AssignedToUserName ?? b.assignedToUserName) : (b.AssignedByUserName ?? b.assignedByUserName)) ?? '';
          return String(va).toLowerCase().localeCompare(String(vb).toLowerCase(), 'tr');
        }
        case 'status': {
          const sa = a.Status ?? a.status ?? 1;
          const sb = b.Status ?? b.status ?? 1;
          return sa - sb;
        }
        default: return 0;
      }
    };
    list.sort((a, b) => (sortDirection === 'asc' ? 1 : -1) * cmp(a, b));
    return list;
  }, [filteredTasks, sortColumn, sortDirection, user]);

  const handleSort = (column) => {
    if (sortColumn === column) setSortDirection(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortColumn(column); setSortDirection('asc'); }
  };

  const SortArrow = ({ column }) => (
    <span className="tasks-sort-arrows">
      {sortColumn !== column ? (
        <><span className="tasks-sort-arrow">▲</span><span className="tasks-sort-arrow">▼</span></>
      ) : sortDirection === 'asc' ? (
        <span className="tasks-sort-arrow active">▲</span>
      ) : (
        <span className="tasks-sort-arrow active">▼</span>
      )}
    </span>
  );

  const handleCreate = async (taskData) => {
    setLoading(true);
    try {
      await taskAPI.create(taskData);
      await loadData();
      setShowForm(false);
      alert('Görev başarıyla oluşturuldu');
    } catch (error) {
      console.error('Error creating task:', error);
      alert('Hata: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async (taskData) => {
    setLoading(true);
    try {
      await taskAPI.update(taskData);
      await loadData();
      setEditingTask(null);
      setEditingTaskMode('complete');
      window.dispatchEvent(new CustomEvent('taskUpdated', { detail: { taskId: taskData.TaskId } }));
      if (taskData.Status === TASK_STATUS.Completed) {
        alert('Görev tamamlandı olarak işaretlendi.');
      } else if (taskData.Response) {
        alert('Görev kontrole gönderildi');
      } else {
        alert('Görev güncellendi.');
      }
    } catch (error) {
      console.error('Error updating task:', error);
      alert('Hata: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleApproveTask = async (task) => {
    if (!await onayla('Bu görevi onaylayıp tamamlandı olarak işaretlemek istediğinizden emin misiniz?')) {
      return;
    }
    setLoading(true);
    try {
      await taskAPI.update({
        TaskId: task.Id ?? task.id,
        Status: TASK_STATUS.Completed
      });
      await loadData();
      window.dispatchEvent(new CustomEvent('taskUpdated', { detail: { taskId: task.Id ?? task.id } }));
      alert('Görev başarıyla onaylandı');
    } catch (error) {
      console.error('Error approving task:', error);
      alert('Hata: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTask = async (task) => {
    if (!await onayla('Bu görevi silmek istediğinize emin misiniz?')) return;
    setLoading(true);
    try {
      await taskAPI.delete(task.Id ?? task.id);
      await loadData();
      window.dispatchEvent(new CustomEvent('taskUpdated', { detail: { taskId: task.Id ?? task.id } }));
      alert('Görev silindi.');
    } catch (error) {
      console.error('Error deleting task:', error);
      alert('Hata: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleRejectTask = async () => {
    if (!rejectForm.rejectionReason.trim()) {
      alert('Lütfen reddedilme sebebini giriniz.');
      return;
    }
    setLoading(true);
    try {
      const newDueDateValue = rejectForm.newDueDate && /^\d{4}-\d{2}-\d{2}$/.test(rejectForm.newDueDate)
        ? `${rejectForm.newDueDate}T23:59`
        : rejectForm.newDueDate;
      await taskAPI.reject(
        rejectingTask.Id ?? rejectingTask.id,
        rejectForm.rejectionReason,
        toISODateOrNull(newDueDateValue)
      );
      await loadData();
      setRejectingTask(null);
      setRejectForm({ rejectionReason: '', newDueDate: '' });
      window.dispatchEvent(new CustomEvent('taskUpdated', { detail: { taskId: rejectingTask.Id ?? rejectingTask.id } }));
      alert('Görev reddedildi ve yeni görev atandı');
    } catch (error) {
      console.error('Error rejecting task:', error);
      alert('Hata: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return '';
    // UTC+3 (Istanbul) timezone için formatDateForDisplay kullan
    return formatDateForDisplay(date);
  };

  const getTabLabel = (status) => {
    if (status === 'all') return 'Tümü';
    const num = parseInt(status, 10);
    return TASK_STATUS_LABELS[num] ?? 'Tümü';
  };

  const handlePrint = () => {
    if (loading || sortedTasks.length === 0) {
      alert('Yazdırılacak liste boş.');
      return;
    }
    const esc = (s) => {
      if (s == null || s === '') return '';
      const t = String(s);
      return t.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    };
    const pageTitle = isAdmin() ? 'Görev Yönetimi' : 'Bana Atanan Görevler';
    const title = `${pageTitle} – ${getTabLabel(filterStatus)}`;
    const assignedHeader = isAdmin() ? 'Atanan' : 'Atayan';
    const thead = `
      <tr>
        <th>Föy No</th>
        <th>Müvekkil</th>
        <th>Açıklama</th>
        <th>Son Tarih</th>
        <th>${esc(assignedHeader)}</th>
        <th>Durum</th>
      </tr>`;
    const rows = sortedTasks.map((task) => {
      const foyNo = task.FoyNo ?? task.foyNo ?? '-';
      const muvekkil = task.Muvekkil ?? task.muvekkil ?? '-';
      const description = task.Description ?? task.description ?? '-';
      const dueDate = task.DueDate ?? task.dueDate;
      const assignedTo = task.AssignedToUserName ?? task.assignedToUserName ?? '-';
      const assignedBy = task.AssignedByUserName ?? task.assignedByUserName ?? '-';
      const assigned = isAdmin() ? assignedTo : assignedBy;
      const statusNum = parseInt(task.Status ?? task.status ?? 1);
      const statusLabel = TASK_STATUS_LABELS[statusNum] ?? 'Bilinmiyor';
      const dueStr = dueDate ? formatDateForDisplay(dueDate) : '-';
      return `<tr>
        <td>${esc(foyNo)}</td>
        <td>${esc(muvekkil)}</td>
        <td>${esc(description)}</td>
        <td>${esc(dueStr)}</td>
        <td>${esc(assigned)}</td>
        <td>${esc(statusLabel)}</td>
      </tr>`;
    }).join('');
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${esc(title)}</title><style>
      body { font-family: Arial, sans-serif; font-size: 12px; padding: 16px; color: #333; }
      h1 { margin: 0 0 12px 0; font-size: 18px; }
      table { width: 100%; border-collapse: collapse; }
      th, td { border: 1px solid #ddd; padding: 8px 10px; text-align: left; }
      th { background: #455a64; color: #fff; font-weight: 600; }
      tr:nth-child(even) { background: #f9f9f9; }
    </style></head><body><h1>${esc(title)}</h1><table><thead>${thead}</thead><tbody>${rows}</tbody></table></body></html>`;
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('Açılır pencere engellendi. Lütfen yazdırma için izin verin.');
      return;
    }
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.onafterprint = () => printWindow.close();
    }, 250);
  };

  return (
    <div className="tasks-page">
      <div className="tasks-header">
        <h1>{isAdmin() ? 'Görev Yönetimi' : 'Bana Atanan Görevler'}</h1>
        {isAdmin() && (
          <button 
            className="btn btn-primary btn-sm" 
            onClick={() => setShowForm(true)}
          >
            Yeni Görev Ata
          </button>
        )}
      </div>

      {!isAdmin() && pendingCompleteCount > 0 && (
        <div className="tasks-info-banner tasks-banner-complete">
          <strong>{pendingCompleteCount}</strong> görev tamamlanmayı bekliyor. İşlemde sekmesinden &quot;Kontrole Gönder&quot; butonuna tıklayın.
        </div>
      )}
      {isAdmin() && pendingReviewCount > 0 && (
        <div className="tasks-info-banner tasks-banner-review">
          <strong>{pendingReviewCount}</strong> görev onayınızı bekliyor. Kontrol Ediliyor sekmesinden Onayla/Reddet butonlarına tıklayın.
        </div>
      )}

      <div className="tasks-filters">
        <input
          type="text"
          placeholder="Foy No, Müvekkil veya Açıklama ile ara..."
          className="form-control tasks-search"
          value={filters.search}
          onChange={(e) => setFilters({ ...filters, search: e.target.value })}
        />
      </div>

      <div className="tasks-tabs-row">
        <div className="tasks-tabs">
          <button
            type="button"
            className={`tasks-tab ${filterStatus === 'all' ? 'active' : ''}`}
            onClick={() => setFilterStatus('all')}
          >
            Tümü <span className="tasks-tab-count">({tabCounts.all})</span>
          </button>
          <button
            type="button"
            className={`tasks-tab ${filterStatus === String(TASK_STATUS.InProgress) ? 'active' : ''}`}
            onClick={() => setFilterStatus(String(TASK_STATUS.InProgress))}
          >
            {TASK_STATUS_LABELS[TASK_STATUS.InProgress]} <span className="tasks-tab-count">({tabCounts.inProgress})</span>
          </button>
          <button
            type="button"
            className={`tasks-tab ${filterStatus === String(TASK_STATUS.UnderReview) ? 'active' : ''}`}
            onClick={() => setFilterStatus(String(TASK_STATUS.UnderReview))}
          >
            {TASK_STATUS_LABELS[TASK_STATUS.UnderReview]} <span className="tasks-tab-count">({tabCounts.underReview})</span>
          </button>
          <button
            type="button"
            className={`tasks-tab ${filterStatus === String(TASK_STATUS.Completed) ? 'active' : ''}`}
            onClick={() => setFilterStatus(String(TASK_STATUS.Completed))}
          >
            {TASK_STATUS_LABELS[TASK_STATUS.Completed]} <span className="tasks-tab-count">({tabCounts.completed})</span>
          </button>
          <button
            type="button"
            className={`tasks-tab ${filterStatus === String(TASK_STATUS.Rejected) ? 'active' : ''}`}
            onClick={() => setFilterStatus(String(TASK_STATUS.Rejected))}
          >
            {TASK_STATUS_LABELS[TASK_STATUS.Rejected]} <span className="tasks-tab-count">({tabCounts.rejected})</span>
          </button>
        </div>
        <button type="button" className="tasks-print-btn" onClick={handlePrint} title="Seçili listeyi yazdır">
          Yazdır
        </button>
      </div>

      {loading ? (
        <div className="loading">Yükleniyor...</div>
      ) : filteredTasks.length === 0 ? (
        YENI_TASARIM
          ? <BosDurum simge="liste" baslik="Görev yok" metin="Bu listede gösterilecek görev bulunmuyor." />
          : <div className="empty-state">
          <p>Görev bulunamadı</p>
        </div>
      ) : (
        <div className="tasks-table-container">
          <table className="tasks-table">
            <thead>
              <tr>
                <th className="tasks-th-sortable" onClick={() => handleSort('foyNo')}><span className="tasks-th-content"><span>Föy No</span><SortArrow column="foyNo" /></span></th>
                <th className="tasks-th-sortable" onClick={() => handleSort('muvekkil')}><span className="tasks-th-content"><span>Müvekkil</span><SortArrow column="muvekkil" /></span></th>
                <th className="tasks-th-sortable" onClick={() => handleSort('description')}><span className="tasks-th-content"><span>Açıklama</span><SortArrow column="description" /></span></th>
                <th className="tasks-th-sortable" onClick={() => handleSort('dueDate')}><span className="tasks-th-content"><span>Son Tarih</span><SortArrow column="dueDate" /></span></th>
                <th className="tasks-th-sortable" onClick={() => handleSort('assigned')}><span className="tasks-th-content"><span>{isAdmin() ? 'Atanan' : 'Atayan'}</span><SortArrow column="assigned" /></span></th>
                <th className="tasks-th-sortable" onClick={() => handleSort('status')}><span className="tasks-th-content"><span>Durum</span><SortArrow column="status" /></span></th>
                <th>İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {sortedTasks.map((task) => {
                const status = parseInt(task.Status ?? task.status ?? 1);
                const foyNo = task.FoyNo ?? task.foyNo;
                const muvekkil = task.Muvekkil ?? task.muvekkil;
                const description = task.Description ?? task.description;
                const assignedBy = task.AssignedByUserName ?? task.assignedByUserName;
                const assignedTo = task.AssignedToUserName ?? task.assignedToUserName;
                const taskId = task.Id ?? task.id;
                const caseFileId = task.CaseFileId ?? task.caseFileId;
                const dueDate = task.DueDate ?? task.dueDate;

                let isOverdue = false;
                if (dueDate && status !== TASK_STATUS.Completed && status !== TASK_STATUS.Rejected) {
                  try {
                    const dueDateObj = typeof dueDate === 'string' ? new Date(dueDate) : new Date(dueDate);
                    isOverdue = dueDateObj < new Date();
                  } catch (e) {}
                }

                return (
                  <tr key={taskId}>
                    <td>
                      <button
                        type="button"
                        className="tasks-link"
                        onClick={() => navigate(`/case-files/${caseFileId}`)}
                      >
                        {foyNo ?? '-'}
                      </button>
                    </td>
                    <td className="tasks-muvekkil" title={muvekkil ?? ''}>{muvekkil ?? '-'}</td>
                    <td className="tasks-desc">{description ?? '-'}</td>
                    <td>
                      <span className={isOverdue ? 'tasks-overdue' : ''}>{formatDate(dueDate)}</span>
                    </td>
                    <td>{isAdmin() ? (assignedTo ?? '-') : (assignedBy ?? '-')}</td>
                    <td>
                      <span className={`tasks-status-badge status-${status}`}>
                        {TASK_STATUS_LABELS[status] || 'Bilinmiyor'}
                      </span>
                    </td>
                    <td>
                      <div className="tasks-actions">
                        {!isAdmin() && status === TASK_STATUS.InProgress && (
                          <ActionIcon type="complete" title="Kontrole Gönder" onClick={(e) => { e.stopPropagation(); setEditingTask(task); }} />
                        )}
                        {isAdmin() && status === TASK_STATUS.InProgress && (
                          <ActionIcon type="complete" title="Tamamlandı Yap" onClick={(e) => { e.stopPropagation(); setEditingTaskMode('complete'); setEditingTask(task); }} />
                        )}
                        {isAdmin() && (
                          <>
                            <ActionIcon type="edit" title="Düzenle" onClick={(e) => { e.stopPropagation(); setEditingTaskMode('edit'); setEditingTask(task); }} />
                            <ActionIcon type="delete" title="Sil" onClick={(e) => { e.stopPropagation(); handleDeleteTask(task); }} />
                          </>
                        )}
                        {isAdmin() && status === TASK_STATUS.UnderReview && (
                          <>
                            <ActionIcon type="approve" title="Onayla" onClick={(e) => { e.stopPropagation(); handleApproveTask(task); }} />
                            <ActionIcon type="reject" title="Reddet" onClick={(e) => { e.stopPropagation(); setRejectingTask(task); }} />
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <TaskForm
          caseFiles={caseFiles}
          users={users}
          onSubmit={handleCreate}
          onCancel={() => setShowForm(false)}
          loading={loading}
        />
      )}

      {editingTask && (
        <TaskForm
          task={editingTask}
          caseFiles={caseFiles}
          users={users}
          onSubmit={handleUpdate}
          onCancel={() => { setEditingTask(null); setEditingTaskMode('complete'); }}
          loading={loading}
          editMode={editingTaskMode === 'edit'}
        />
      )}

      {rejectingTask && (
        <div className="task-form-overlay">
          <div className="task-form">
            <h2>Görevi Reddet</h2>
            <form onSubmit={(e) => { e.preventDefault(); handleRejectTask(); }}>
              <div className="form-group">
                <label>Reddedilme Sebebi *</label>
                <textarea
                  value={rejectForm.rejectionReason}
                  onChange={(e) => setRejectForm({ ...rejectForm, rejectionReason: e.target.value })}
                  className="form-control"
                  rows="4"
                  placeholder="Görevin neden reddedildiğini açıklayın..."
                  required
                />
              </div>
              <div className="form-group">
                <label>Yeni Görev Son Tarihi</label>
                <input
                  type="date"
                  value={rejectForm.newDueDate}
                  onChange={(e) => setRejectForm({ ...rejectForm, newDueDate: e.target.value })}
                  className="form-control"
                  min={getTodayDateString()}
                />
              </div>
              <div className="form-actions">
                <button 
                  type="button" 
                  onClick={() => {
                    setRejectingTask(null);
                    setRejectForm({ rejectionReason: '', newDueDate: '' });
                  }} 
                  className="btn btn-secondary"
                >
                  İptal
                </button>
                <button type="submit" className="btn btn-danger" disabled={loading}>
                  {loading ? 'Reddediliyor...' : 'Reddet ve Yeni Görev Ata'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Tasks;

